/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { useState } from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import { MdOutlineSubject } from 'react-icons/md';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { IMoeVimeoPlayerViewProps } from './moe-vimeo-player';
import { FaFileDownload } from 'react-icons/fa';
import { KWR, VIDEO_PLAYER } from '../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage } from '../../utilities/language-change';

export default (props: IMoeVimeoPlayerViewProps) => {
    const { config, context, resources } = props;

    const [showTranscript, setShowTranscript] = useState(false);

    // Identifying Language
    const toggleLang = getToggleLang(context);
    const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
    const preferredLanguage: any = getUserPreferredLanguage(KWR, VIDEO_PLAYER, userPrefLang, toggleLang, context);

    function handleParagraphChange(event: MsDyn365.ContentEditableEvent): void {
        props.config.transcript = event.target.value;
    }

    function _getVideoDetailsParagraph(): React.ReactNode | undefined {
        const { transcript } = props.config;

        return (
            transcript && (
                <MsDyn365.RichTextComponent
                    text={transcript}
                    className='ms-video-player__details__text'
                    editProps={{ onEdit: handleParagraphChange, requestContext: props.context.request }}
                />
            )
        );
    }

    function downloadTranscript() {
        const element = document.createElement('a');
        let data: any = props.config.transcript;
        const audioTextElement = document.createElement('p');
        audioTextElement.innerHTML = data;
        data = audioTextElement.textContent;
        const file = new Blob([data], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'vimeo_transcript.txt';
        document.body.appendChild(element); // Required for this to work in FireFox
        element.click();
        document.body.removeChild(element);
    }

    return (
        <div tabIndex={0} className='moe-vimeo-player'>
            <h2 className='moe-vimeo-player__ttl'>{config.videoTitle}</h2>
            <div className='moe-vimeo-player__iframe-cont'>
                <iframe
                    className='moe-vimeo-player__iframe'
                    src={config.linkText}
                    frameBorder='0'
                    allow='autoplay; fullscreen; picture-in-picture'
                    title={config.videoTitle}
                ></iframe>
                <div className='ms-video-transcript'>
                    <div className='ms-video-transcript__top-container'>
                        <div className='ms-video-transcript__top-left'>
                            <MdOutlineSubject className='ms-video-transcript__icon' />
                            <span>
                                {preferredLanguage === 'maori' ? resources.videoTranscriptTextInMaori : resources.videoTranscriptText}
                            </span>
                        </div>
                        <div className='ms-video-transcript__top-right'>
                            <FaFileDownload
                                onClick={() => downloadTranscript()}
                                className='ms-video-transcript__download-icon tooltip-test'
                                title={preferredLanguage === 'maori' ? resources.videoTranscriptTextInMaori : resources.videoTranscriptText}
                            />
                            <AiOutlinePlus
                                onClick={() => setShowTranscript(!showTranscript)}
                                className='ms-video-transcript__plus-icon'
                                style={{
                                    transform: showTranscript ? 'rotate(45deg)' : 'rotate(0deg)',
                                    transition: 'all 0.3s ease-in-out',
                                    cursor: 'pointer',
                                    fontSize: '1rem'
                                }}
                                title={preferredLanguage === 'maori' ? resources.videoTranscriptTextInMaori : resources.videoTranscriptText}
                            />
                        </div>
                    </div>
                    <div className='ms-video-transcript__btm-container'>
                        <div>{showTranscript && <p>{_getVideoDetailsParagraph()}</p>}</div>
                    </div>
                </div>
            </div>
            <script src='https://player.vimeo.com/api/player.js' defer></script>
        </div>
    );
};
